import React from 'react';
import {
  Button,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  Divider
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { GoogleLogin } from '@react-oauth/google';
import TextInput from 'components/atoms/textInput';
import PassInput from 'components/atoms/passInput';

const SignupFormView = ({ onSubmit, onGoogleLogin, isLoading, setShowLogin }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Signup validation schema
  const signupSchema = yup.object().shape({
    first_name: yup
      .string()
      .required(t('signup.firstNameRequired'))
      .min(2, t('signup.firstNameTooShort')),
    last_name: yup
      .string()
      .required(t('signup.lastNameRequired'))
      .min(2, t('signup.lastNameTooShort')),
    email: yup
      .string()
      .email(t('signup.invalidEmail'))
      .required(t('signup.emailRequired')),
    password: yup
      .string()
      .required(t('signup.passwordRequired'))
      .min(8, t('signup.passwordTooShort'))
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&._-])[A-Za-z\d@$!%*?&._-]{8,}$/,
        t('signup.passwordComplexity')
      ),
    privacyPolicyChecked: yup
      .boolean()
      .oneOf([true], t('signup.privacyPolicyRequired'))
  });

  const {
    handleSubmit,
    control,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(signupSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      privacyPolicyChecked: false
    }
  });

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%'
      }}
    >
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextInput
          label={t('signup.firstName')}
          name='first_name'
          control={control}
          error={errors?.first_name}
        />
        <TextInput
          label={t('signup.lastName')}
          name='last_name'
          control={control}
          error={errors?.last_name}
        />
      </Box>

      <TextInput
        label={t('signup.emailLabel')}
        name='email'
        control={control}
        error={errors?.email}
      />

      <PassInput
        label={t('signup.passwordLabel')}
        name='password'
        control={control}
        error={errors?.password}
      />

      <Controller
        name="privacyPolicyChecked"
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={
              <Checkbox
                {...field}
                checked={field.value}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                {t('signup.privacyPolicyAgreement')}{' '}
                <Typography
                  component="span"
                  color="primary"
                  sx={{
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                  onClick={() => navigate('/terms-of-service')}
                >
                  {t('signup.privacyPolicy')}
                </Typography>
              </Typography>
            }
          />
        )}
      />

      {errors.privacyPolicyChecked && (
        <Typography
          color="error"
          variant="body2"
          sx={{ mt: -1, ml: 2 }}
        >
          {errors.privacyPolicyChecked.message}
        </Typography>
      )}

      <Button
        type="submit"
        variant="contained"
        color="primary"
        fullWidth
        disabled={isLoading}
        sx={{ py: 1 }}
      >
        {isLoading ? t('common.loading') : t('signup.createAccount')}
      </Button>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          my: 0.5
        }}
      >
        <Divider sx={{ flexGrow: 1 }} />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mx: 2 }}
        >
          {t('login.or')}
        </Typography>
        <Divider sx={{ flexGrow: 1 }} />
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          '& > div': {
            display: 'flex !important',
            justifyContent: 'center !important',
            width: '100% !important'
          },
          '& > div > div': {
            margin: '0 auto !important'
          }
        }}
      >
        <GoogleLogin
          onSuccess={onGoogleLogin}
          onError={() => {}}
          theme="filled_blue"
          text="signup_with"
          shape="rectangular"
          width="100%"
        />
      </Box>

      <Box sx={{ textAlign: 'center', mt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {t('signup.alreadyHaveAccount')}{' '}
          <Typography
            component="span"
            color="primary"
            sx={{
              cursor: 'pointer',
              fontWeight: 600
            }}
            onClick={() => setShowLogin(true)}
          >
            {t('signup.loginHere')}
          </Typography>
        </Typography>
      </Box>
    </Box>
  );
};

export default SignupFormView;